/* ============================================================================
   m-custom-lightning.js
   ----------------------------------------------------------------------------
   Branch-local lightning-sale JS, split OUT of assets/m-custom.js so that the
   shared m-custom.js stays byte-identical to master and does not produce merge
   conflicts. Loaded right after m-custom.js in layout/theme.liquid.

   This file is fully self-contained — it does NOT rely on any function inside
   m-custom.js. Where m-custom.js previously had inline calls to
   syncGridLightning() inside updateVariant() / attachVariantLogic() / the
   sibling-select handler, we reproduce that behaviour here with our own
   delegated listeners + MutationObserver, keyed off the same <select> elements
   and data-ls-* attributes.

   Two independent IIFEs:
     1. Grid-card lightning sync — reveals/hides each card's top countdown for
        the selected variant.
     2. Shared countdown engine — ticks every [data-lightning-timer] on the
        page (PDP pills, cart-drawer items, grid strips) once per second.
   ============================================================================ */

/* ---------------------------------------------------------------------------
   1. Grid-card lightning sync
   --------------------------------------------------------------------------- */
(function () {
  // Resolve the card wrapper a given select/option lives in. Mirrors the
  // getSiblingCard() resolution order used in m-custom.js.
  function getCard(el) {
    return (
      el.closest(".m-product-card-wrapper") ||
      el.closest(".product-bx") ||
      el.closest(".home-cat-iteam") ||
      el.closest(".prduct-filter") ||
      el.closest("form")
    );
  }

  /* Reveal the grid card's lightning countdown when the chosen variant is on
     sale, hide it otherwise. Reads data-ls-mode / data-ls-end / data-ls-start
     from the source element (a selected <option> or the single-variant <select>)
     and copies them onto the shell's [data-lightning-timer] so the shared timer
     loop paints + expires it like every other badge. */
  function syncGridLightning(card, source) {
    if (!card) return;
    const shell = card.querySelector("[data-grid-lightning]");
    if (!shell) return;
    const timer = shell.querySelector("[data-lightning-timer]");

    const mode = source && (source.getAttribute("data-ls-mode") || source.dataset.lsMode || source.getAttribute("data-single-ls-mode") || source.dataset.singleLsMode);
    const end = source && (source.getAttribute("data-ls-end") || source.dataset.lsEnd || source.getAttribute("data-single-ls-end") || source.dataset.singleLsEnd);
    const start = source && (source.getAttribute("data-ls-start") || source.dataset.lsStart || source.getAttribute("data-single-ls-start") || source.dataset.singleLsStart);

    if (mode) {
      if (timer) {
        timer.setAttribute("data-lt-mode", mode);
        if (end) timer.setAttribute("data-lt-end", end); else timer.removeAttribute("data-lt-end");
        if (start) timer.setAttribute("data-lt-start", start); else timer.removeAttribute("data-lt-start");
        // A previously-expired sale hides the timer (display:none); clear that so
        // re-selecting a live lightning variant shows it again.
        timer.style.display = "";
      }
      shell.classList.remove("is-hidden");
    } else {
      shell.classList.add("is-hidden");
      // Strip the end so the shared loop doesn't keep ticking a hidden badge.
      if (timer) timer.removeAttribute("data-lt-end");
    }
  }

  // Sync a card from a <select> element's currently-selected option (multi-
  // variant dropdowns: .m_variant_select and the sibling .m-sibling-select).
  function syncFromSelect(select) {
    if (!select || !select.options) return;
    const option = select.options[select.selectedIndex];
    if (!option) return;
    syncGridLightning(getCard(select), option);
  }

  // Initial pass over a scope: multi-variant dropdowns sync from their selected
  // option; single-variant cards sync from their lone <select>.
  function initScope(root) {
    const scope = root && root.querySelectorAll ? root : document;

    const selects = scope.querySelectorAll(".m_variant_select, .m-sibling-select");
    for (let i = 0; i < selects.length; i++) syncFromSelect(selects[i]);

    const singles = scope.querySelectorAll(".m_variant_select-single");
    for (let j = 0; j < singles.length; j++) {
      syncGridLightning(getCard(singles[j]), singles[j]);
    }
  }

  // Delegated change/input — covers server-rendered cards AND AJAX-injected
  // ones (infinite scroll, facet refresh). Capture phase so we run regardless
  // of other handlers stopping propagation.
  function handleSelectEvent(event) {
    const el = event.target;
    if (!el || !el.classList) return;
    if (el.classList.contains("m_variant_select") || el.classList.contains("m-sibling-select")) {
      syncFromSelect(el);
    } else if (el.classList.contains("m_variant_select-single")) {
      syncGridLightning(getCard(el), el);
    }
  }
  document.addEventListener("change", handleSelectEvent, true);
  document.addEventListener("input", handleSelectEvent, true);

  // Initial run + observe for cards mounted after load.
  function boot() {
    initScope(document);
    if (!document.body) return;
    const observer = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const nodes = mutations[i].addedNodes || [];
        for (let j = 0; j < nodes.length; j++) {
          if (nodes[j].nodeType === 1) initScope(nodes[j]);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();


/* ---------------------------------------------------------------------------
   2. Shared lightning-sale countdown engine
   ---------------------------------------------------------------------------
   Drives every [data-lightning-timer] badge on the page — PDP size pills AND
   cart-drawer line items AND grid strips. Each badge declares its mode via
   data-lt-mode, set from the product's custom.lightning_sale metaobject:

     mode="24h"   → rolling 24h that resets at local midnight (12am). Counts the
                    time remaining until the next 00:00:00, so at 12am it snaps
                    back to a fresh 24h. (24_hour = true on the metaobject.)
     mode="range" → real countdown to data-lt-end (the metaobject's end_time).
                    When end_time has passed, the badge is hidden.

   A single shared interval ticks all badges in sync. The shared interval also
   re-scans the DOM each tick so cart-drawer items re-rendered by the Section
   Rendering API pick up the timer without re-init. */
(function () {
  function pad(n) {
    return n < 10 ? "0" + n : "" + n;
  }

  // ms remaining from `now` until the next local midnight.
  function msUntilMidnight(now) {
    var next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
    return next.getTime() - now.getTime();
  }

  // Parse "YYYY-MM-DDTHH:MM:SS" as LOCAL time (no trailing Z) into ms.
  function parseEnd(str) {
    if (!str) return NaN;
    var m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(str);
    if (m) {
      return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]).getTime();
    }
    var t = Date.parse(str); // fall back to native parse for other formats
    return t;
  }

  // Compute ms remaining for one timer element based on its mode. Returns -1
  // when a ranged sale has expired (so the caller can hide it).
  function remainingFor(t, now) {
    var mode = t.getAttribute("data-lt-mode") || "24h";
    if (mode === "range") {
      var end = parseEnd(t.getAttribute("data-lt-end"));
      if (isNaN(end)) return msUntilMidnight(now); // no end → behave like daily loop
      var diff = end - now.getTime();
      return diff <= 0 ? -1 : diff;
    }
    var rem = msUntilMidnight(now);
    return rem < 0 ? 0 : rem;
  }

  function paint(t, remaining) {
    var dEl = t.querySelector("[data-lt-d]");
    var hEl = t.querySelector("[data-lt-h]");
    var mEl = t.querySelector("[data-lt-m]");
    var sEl = t.querySelector("[data-lt-s]");

    var totalSec = Math.floor(remaining / 1000);
    var d = Math.floor(totalSec / 86400);
    var h = Math.floor((totalSec % 86400) / 3600);
    var m = Math.floor((totalSec % 3600) / 60);
    var s = totalSec % 60;

    // Hide the day segment when there are no full days left, so the badge reads
    // "⚡ 03h 58m 20s". Markup stays in place for multi-day sales.
    if (dEl) {
      dEl.textContent = d;
      var dSeg = dEl.closest("[class*='timer-seg']");
      if (dSeg) dSeg.style.display = d > 0 ? "" : "none";
    }
    if (hEl) hEl.textContent = pad(h);
    if (mEl) mEl.textContent = pad(m);
    if (sEl) sEl.textContent = pad(s);
  }

  function render() {
    var timers = document.querySelectorAll("[data-lightning-timer]");
    if (!timers.length) return false;

    var now = new Date();
    for (var i = 0; i < timers.length; i++) {
      var t = timers[i];
      var remaining = remainingFor(t, now);
      if (remaining < 0) {
        // Expired ranged sale — hide the badge entirely. For grid cards, hide
        // the whole strip (label + bg), not just the digits, so an expired sale
        // leaves no empty green bar behind.
        t.style.display = "none";
        var gridShell = t.closest("[data-grid-lightning]");
        if (gridShell) gridShell.classList.add("is-hidden");
        continue;
      }
      paint(t, remaining);
    }
    return true;
  }

  function start() {
    render();
    // Always tick: cart-drawer items can mount after load, so we keep scanning.
    window.setInterval(render, 1000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
